import { useMemo } from 'react'
import { elevationProfiles } from '../utils/elevationProfiles'

interface Props {
  legNumber: number
  onClose: () => void
}

export function ElevationChart({ legNumber, onClose }: Props) {
  const profile = elevationProfiles[legNumber]

  const chart = useMemo(() => {
    if (!profile || profile.length < 2) return null

    const W = 320
    const H = 160
    const PAD_L = 42
    const PAD_R = 8
    const PAD_T = 12
    const PAD_B = 28

    const plotW = W - PAD_L - PAD_R
    const plotH = H - PAD_T - PAD_B

    const maxDist = profile[profile.length - 1][0]
    const elevations = profile.map(p => p[1])
    const minElev = Math.min(...elevations)
    const maxElev = Math.max(...elevations)
    const elevRange = maxElev - minElev || 100

    // Add 5% padding to elevation range
    const padElev = elevRange * 0.05
    const eMin = minElev - padElev
    const eMax = maxElev + padElev
    const eRange = eMax - eMin

    const toX = (d: number) => PAD_L + (d / maxDist) * plotW
    const toY = (e: number) => PAD_T + plotH - ((e - eMin) / eRange) * plotH

    // Build area path (filled)
    const points = profile.map(p => `${toX(p[0]).toFixed(1)},${toY(p[1]).toFixed(1)}`)
    const areaPath = `M${toX(0).toFixed(1)},${toY(profile[0][1]).toFixed(1)} ` +
      points.map(p => `L${p}`).join(' ') +
      ` L${toX(maxDist).toFixed(1)},${(PAD_T + plotH).toFixed(1)} L${PAD_L},${(PAD_T + plotH).toFixed(1)} Z`

    const linePath = `M${points.join(' L')}`

    // Y-axis ticks (4-5 ticks)
    const yTicks: number[] = []
    const niceStep = Math.ceil(elevRange / 4 / 100) * 100
    const yStart = Math.ceil(minElev / niceStep) * niceStep
    for (let y = yStart; y <= maxElev; y += niceStep) yTicks.push(y)

    // X-axis ticks
    const xTicks: number[] = []
    const xStep = maxDist <= 3 ? 0.5 : maxDist <= 6 ? 1 : 2
    for (let x = 0; x <= maxDist; x += xStep) xTicks.push(x)
    if (xTicks[xTicks.length - 1] < maxDist - xStep * 0.3) xTicks.push(maxDist)

    return { W, H, PAD_L, PAD_T, PAD_B, plotW, plotH, areaPath, linePath, toX, toY, yTicks, xTicks, minElev: Math.round(minElev), maxElev: Math.round(maxElev), maxDist }
  }, [profile])

  if (!chart) {
    return (
      <div className="text-sm text-gray-400 p-4">No elevation data for leg {legNumber}.</div>
    )
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl mx-4 w-full max-w-sm p-4"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold">Leg {legNumber} Elevation Profile</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
          {chart.minElev.toLocaleString()}&ndash;{chart.maxElev.toLocaleString()} ft &middot; {chart.maxDist.toFixed(1)} mi
        </div>

        <svg viewBox={`0 0 ${chart.W} ${chart.H}`} className="w-full" style={{ aspectRatio: `${chart.W}/${chart.H}` }}>
          {/* Grid lines */}
          {chart.yTicks.map(y => (
            <g key={y}>
              <line
                x1={chart.PAD_L} y1={chart.toY(y)} x2={chart.PAD_L + chart.plotW} y2={chart.toY(y)}
                stroke="currentColor" className="text-gray-200 dark:text-gray-600" strokeWidth={0.5}
              />
              <text
                x={chart.PAD_L - 4} y={chart.toY(y) + 3}
                textAnchor="end" className="fill-gray-400 dark:fill-gray-500" fontSize={8}
              >
                {y.toLocaleString()}
              </text>
            </g>
          ))}

          {/* X-axis ticks */}
          {chart.xTicks.map(x => (
            <text
              key={x} x={chart.toX(x)} y={chart.H - 4}
              textAnchor="middle" className="fill-gray-400 dark:fill-gray-500" fontSize={8}
            >
              {x.toFixed(x % 1 === 0 ? 0 : 1)}
            </text>
          ))}

          {/* Area fill */}
          <path d={chart.areaPath} className="fill-green-200/60 dark:fill-green-900/40" />

          {/* Elevation line */}
          <path d={chart.linePath} fill="none" className="stroke-green-600 dark:stroke-green-400" strokeWidth={1.5} />

          {/* Axis labels */}
          <text x={chart.W / 2} y={chart.H - 0} textAnchor="middle" className="fill-gray-500 dark:fill-gray-400" fontSize={7}>
            Distance (mi)
          </text>
          <text
            x={4} y={chart.PAD_T + chart.plotH / 2}
            textAnchor="middle" className="fill-gray-500 dark:fill-gray-400" fontSize={7}
            transform={`rotate(-90, 4, ${chart.PAD_T + chart.plotH / 2})`}
          >
            Elevation (ft)
          </text>
        </svg>
      </div>
    </div>
  )
}
